import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import HubLiveScores from "./HubLiveScores";
import HubNews from "./HubNews";
import HubTransfers from "./HubTransfers";
import YektanetBanner, { YEKTANET_HOME_POS_ID } from "../YektanetBanner";
import { useTranslation } from "../../hooks/useTranslation";
import { HUB_PAGE_BG, hub } from "./hubSurface";

const HomeHub: React.FC = () => {
  const { t } = useTranslation();

  const quickLinks = [
    { to: "/transfers", label: t("hub.navTransfers"), hint: t("hub.navTransfersHint") },
    { to: "/news", label: t("hub.navNews"), hint: t("hub.navNewsHint") },
    { to: "/livescores", label: t("hub.navLiveScores"), hint: t("hub.navLiveScoresHint") },
    { to: "/feed", label: t("hub.navCommunity"), hint: t("hub.navCommunityHint") },
  ] as const;

  return (
    <div
      className={`relative min-h-[calc(100vh-4rem)] ${hub.page}`}
      style={{ background: HUB_PAGE_BG }}
    >
      {/* Transfers-first CTAs */}
      <section className={`relative border-b ${hub.border}`}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 sm:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-wrap gap-2.5"
          >
            <Link to="/transfers" className={hub.btnPrimary}>
              {t("hub.ctaTransfers")}
            </Link>
            <Link to="/news" className={hub.btnGhost}>
              {t("hub.ctaNews")}
            </Link>
            <Link to="/livescores" className={hub.btnGhost}>
              {t("hub.ctaLiveScores")}
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-7" aria-label={t("hub.quickLinks")}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#e6e4df] rounded-xl overflow-hidden ring-1 ring-[#e6e4df]">
          {quickLinks.map((item, i) => (
            <motion.div
              key={item.to}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, delay: 0.05 * i }}
              className="bg-[#fafaf8]"
            >
              <Link
                to={item.to}
                className="block h-full bg-white px-4 py-4 transition-colors hover:bg-[#f6f5f2]"
              >
                <div className={`text-sm font-semibold ${hub.ink}`}>{item.label}</div>
                <div className={`mt-1 text-xs leading-snug ${hub.faint}`}>{item.hint}</div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mobile: transfers → news → livescores; desktop: livescores sidebar + transfers/news */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-10 lg:items-start">
          <div className="order-1 lg:order-2 lg:col-span-7 xl:col-span-8 space-y-6">
            <HubTransfers />
            <YektanetBanner posId={YEKTANET_HOME_POS_ID} />
          </div>
          <div className="order-3 lg:order-1 lg:col-span-5 xl:col-span-4 lg:row-span-2">
            <HubLiveScores />
          </div>
          <div className="order-2 lg:order-3 lg:col-span-7 xl:col-span-8">
            <HubNews />
          </div>
        </div>
      </section>

      <section className={`border-t ${hub.border} bg-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className={`text-lg font-semibold tracking-tight ${hub.ink}`}>
              {t("hub.communityTitle")}
            </h2>
            <p className={`mt-1.5 text-sm max-w-xl leading-6 ${hub.mute}`}>
              {t("hub.communityBody")}
            </p>
          </div>
          <Link to="/feed" className={`${hub.btnGhost} shrink-0`}>
            {t("hub.communityCta")}
          </Link>
        </div>
      </section>

      <section className={`border-t ${hub.border}`}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-10 sm:pb-12">
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5 }}
            className={`text-[1.85rem] sm:text-4xl md:text-[2.75rem] font-bold tracking-tight leading-[1.2] max-w-3xl ${hub.ink}`}
          >
            {t("hub.eyebrow")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: 0.04 }}
            className={`mt-3 text-base sm:text-lg max-w-2xl leading-7 font-medium ${hub.accent}`}
          >
            {t("hub.headline")}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className={`mt-3 text-sm sm:text-[15px] max-w-xl leading-7 ${hub.mute}`}
          >
            {t("hub.subheadline")}
          </motion.p>
        </div>
      </section>
    </div>
  );
};

export default HomeHub;
